import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { resolvePhoto } from "@/lib/api";
import {
  Settings as SettingsIcon, CreditCard, ShieldCheck, ShieldAlert,
  LogOut, User as UserIcon, ShieldHalf,
} from "lucide-react";

export default function AccountMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  if (!user) return null;
  const photo = user.photos?.[0];

  return (
    <div className="relative" ref={wrapRef}>
      <button
        data-testid="account-menu-btn"
        onClick={() => setOpen((v) => !v)}
        className="w-10 h-10 rounded-full border border-[#E5DFD3] overflow-hidden bg-[#F0EBE1] flex items-center justify-center hover:ring-2 hover:ring-[#913831]/30 transition-all"
        aria-label="Account menu"
      >
        {photo ? (
          <img src={resolvePhoto(photo)} alt="" className="w-full h-full object-cover" />
        ) : (
          <UserIcon size={16} className="text-[#737373]" />
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-12 z-40 w-64 bg-white border border-[#E5DFD3] rounded-2xl shadow-[0_25px_50px_-20px_rgba(0,0,0,0.18)] overflow-hidden"
             data-testid="account-menu">
          <div className="px-4 py-3 border-b border-[#E5DFD3] bg-[#F0EBE1]/30">
            <div className="font-serif text-lg text-[#1A1818] truncate flex items-center gap-1.5">
              <span className="truncate">{user.name}</span>
              {user.verified && <ShieldCheck size={14} className="text-[#913831] flex-shrink-0" />}
            </div>
            <div className="text-xs text-[#737373] truncate">{user.email}</div>
          </div>
          <MenuItem to="/settings" testid="menu-settings" icon={<SettingsIcon size={15} />} label="Settings" onClose={() => setOpen(false)} />
          <MenuItem to="/billing" testid="menu-billing"
            icon={<CreditCard size={15} className={user.billing?.quietplus ? "text-[#913831]" : ""} />}
            label={user.billing?.quietplus ? "Quiet+ active" : "Upgrade to Quiet+"}
            onClose={() => setOpen(false)} />
          {!user.verified && (
            <MenuItem to="/verify" testid="menu-verify"
              icon={<ShieldAlert size={15} className="text-[#913831]" />}
              label="Verify your identity" onClose={() => setOpen(false)} />
          )}
          {user.is_admin && (
            <MenuItem to="/admin" testid="menu-admin"
              icon={<ShieldHalf size={15} className="text-[#913831]" />}
              label="Admin console" onClose={() => setOpen(false)} />
          )}
          <button data-testid="menu-logout"
            onClick={() => { logout(); navigate("/"); }}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#F0EBE1] text-left border-t border-[#E5DFD3] text-[#D9534F]">
            <LogOut size={15} /> <span>Sign out</span>
          </button>
        </div>
      )}
    </div>
  );
}

function MenuItem({ to, label, icon, testid, onClose }) {
  return (
    <Link to={to} data-testid={testid} onClick={onClose}
      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#F0EBE1] text-[#1A1818]">
      <span className="w-5 flex justify-center">{icon}</span>
      <span className="flex-1">{label}</span>
    </Link>
  );
}
